import React from 'react';
import css from 'classnames';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';

import LoopStatusIcon from '@mui/icons-material/Repeat';
import MuteStatusIcon from '@mui/icons-material/VolumeMute';

import {
    getPlayerStateFromAction,
    getIconByPlayerStatus,
    getFormattedTime,
    removeFromEvent,
    getCurrentTime,
    attachToEvent,
    getProgress,
} from './utils';

import Player from './utils/constants';

import styles from './styles';

class AudioPlayer extends React.PureComponent
{
  static propTypes = {
      autoPlay: PropTypes.bool,
      audioSrc: PropTypes.string.isRequired,
      width: PropTypes.string,
      height: PropTypes.string,
      rounded: PropTypes.bool,
      classes: PropTypes.object,
      classNames: PropTypes.shape({
          player: PropTypes.string,
          loopIcon: PropTypes.string,
          playIcon: PropTypes.string,
          muteIcon: PropTypes.string,
          slider: PropTypes.string,
          track: PropTypes.string,
          thumb: PropTypes.string,
          text: PropTypes.string,
      }),
  };

  static defaultProps = {
      rounded: false,
      classes: {},
      classNames: {},
      width: '100%',
      height: '100%',
  };

  state = {
      current: 0,
      progress: 0,
      duration: 0,
      loopStatus: Player.Status.UNLOOP,
      playStatus: Player.Status.PAUSE,
      muteStatus: Player.Status.UNMUTE,
  };

  componentDidMount()
  {
      attachToEvent(this.player, Player.Events.CAN_PLAY, this.handleCanPlay);

      if (this.props.autoPlay)
      {
          this.triggerAction(Player.Status.PLAY);
      }
  }

  componentWillUnmount()
  {
      if (this.player)
      {
          removeFromEvent(
              this.player,
              Player.Events.TIME_UPDATE,
              this.handleTimeUpdate,
          );
          removeFromEvent(this.player, Player.Events.CAN_PLAY, this.handleCanPlay);

          this.player = null;
      }
  }

  triggerAction = action =>
  {
      const newState = getPlayerStateFromAction(this.player, action);

      if (newState)
      {
          this.setState(newState);
      }
  };

  handleCanPlay = player =>
  {
      attachToEvent(player, Player.Events.TIME_UPDATE, this.handleTimeUpdate);

      this.setState({
          duration: player.duration,
      });
  };

  handleTimeUpdate = player =>
  {
      this.setState({
          current: player.currentTime,
          progress: getProgress(player.currentTime, player.duration),
      });
  };

  handleChange = (progress, player) =>
  {
      if (player)
      {
          const currentTime = getCurrentTime(progress, player.duration);

          if (!isNaN(currentTime))
          {
              player.currentTime = currentTime;
          }

          this.setState({
              progress,
              currentTime,
          });
      }
  };

  render()
  {
      const {
          rounded,
          width,
          height,
          audioSrc,
          classes,
          classNames: {
              player,
              loopIcon,
              playIcon,
              muteIcon,
              slider,
              track,
              thumb,
              text,
          },
      } = this.props;
      const {
          loopStatus,
          playStatus,
          muteStatus,
          progress,
          current,
          duration,
      } = this.state;

      const PlayStatusIcon = getIconByPlayerStatus(playStatus);

      const isLoopEnable = loopStatus === Player.Status.LOOP;
      const isMuteEnable = muteStatus === Player.Status.MUTE;

      return (
          <React.Fragment>
              <audio
                  ref={node => (this.player = node)}
                  controls={true}
                  hidden={true}
              >
                  <source src={audioSrc} />
              </audio>
              <Paper
                  className={css(classes['player-grid-container'], player)}
                  square={!rounded}
                  elevation={1}
                  style={{
                      width,
                      height,
                  }}
              >
                  <Grid alignItems="center" justifyContent="center" spacing={0} container={true}>
                      <Grid className={classes['player-centered-grid-item']} xs={1} item={true}>
                          <LoopStatusIcon
                              className={css(classes['player-icon-disabled'], loopIcon, {
                                  [classes['player-default-icon']]: isLoopEnable,
                              })}
                              onClick={() => this.triggerAction(Player.Status.LOOP)}
                              focusable={true}
                          />
                      </Grid>
                      <Grid className={classes['player-centered-grid-item']} xs={1} item={true}>
                          <PlayStatusIcon
                              className={css(
                                  classes['player-default-icon'],
                                  classes['player-main-icon'],
                                  playIcon,
                              )}
                              onClick={() => this.triggerAction(Player.Status.PLAY)}
                              focusable={true}
                          />
                      </Grid>
                      <Grid className={classes['player-centered-grid-item']} xs={1} item={true}>
                          <MuteStatusIcon
                              className={css(classes['player-icon-disabled'], muteIcon, {
                                  [classes['player-default-icon']]: isMuteEnable,
                              })}
                              onClick={() => this.triggerAction(Player.Status.MUTE)}
                              focusable={true}
                          />
                      </Grid>
                      <Grid className={classes['player-centered-grid-item']} xs={9} item={true}>
                          <Grid justifyContent="center" spacing={0} direction="row" container={true}>
                              <Grid
                                  className={classes['player-centered-grid-item']}
                                  xs={2}
                                  item={true}
                              >
                                  <Typography
                                      className={css(classes['player-text-timer'], text)}
                                      align="center"
                                      gutterBottom={true}
                                      noWrap={true}
                                  >
                                      {getFormattedTime(current)}
                                  </Typography>
                              </Grid>
                              <Grid
                                  className={classes['player-centered-grid-item']}
                                  xs={8}
                                  item={true}
                              >
                                  <Slider
                                      onChange={(_, _progress) =>
                                          this.handleChange(_progress, this.player)
                                      }
                                      classes={{
                                          root: css(classes['player-slider-container'], slider),
                                          track: css(classes['player-slider-track'], track),
                                          thumb: css(classes['player-slider-thumb'], thumb),
                                      }}
                                      variant="determinate"
                                      color="secondary"
                                      value={progress}
                                  />
                              </Grid>
                              <Grid
                                  className={classes['player-centered-grid-item']}
                                  xs={2}
                                  item={true}
                              >
                                  <Typography
                                      className={css(classes['player-text-timer'], text)}
                                      align="center"
                                      gutterBottom={true}
                                      noWrap={true}
                                  >
                                      {getFormattedTime(duration)}
                                  </Typography>
                              </Grid>
                          </Grid>
                      </Grid>
                  </Grid>
              </Paper>
          </React.Fragment>
      );
  }
}

export default withStyles(styles, { withTheme: true })(AudioPlayer);
